import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { Box, Button, Typography, Paper } from "@mui/material";
import Section from "./Section";
import PreviewForm from "../PreviewForm";
import {
  appendTeamMemberLawyerId,
  pageScrollStyle,
} from "../../../../../styles/styles";
import { toast } from "react-toastify";
import { useDialog } from "../../../../DialogContext";
import { backend_api } from "../../../../../static/server";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { getCaseTemplate } from "../../../../../redux/Apps/Lawyerapp/CaseSlice/CaseSlice";
import { Link } from "react-router-dom";

const FormBuilder = ({
  tabBtn,
  handleTabChange,
  getFormData,
  sections,
  isDragging,
  draggedItemRef,
  activeSectionDropIndex,
  setActiveSectionDropIndex,
  handleSectionDragStart,
  handleFieldDragStart,
  handleDragEnd,
  handleDragOver,
  handleDragEnter,
  handleDragLeave,
  handleSectionDrop,
  handleSectionReorderDrop,
  handleUpdateSectionName,
  handleRemoveSection,
  handleSectionMenuOpen,
  handleFieldDrop,
  handleFieldReorderDrop,
  handleUpdateFieldLabel,
  handleFieldMenuOpen,
  activeFieldDropData,
  setActiveFieldDropData,
  getFieldWidth,
  dragTarget,
  handleUpdateFieldProperty,
  setSections,
  usedFieldsTypes,
  setUsedFieldsTypes,
  isDefaultSection,
  isFieldAvailableForDefaultSection,
  scrollContainerRef,
  isEditMode = false,
  editData = null,
}) => {
  const dispatch = useDispatch();
  const { showDialog, closeAllDialogs } = useDialog();
  const initialized = useRef(false);
  const { token, teamMemberLawyerId } = useSelector((state) => state.user);
  const [quickCreateFields, setQuickCreateFields] = useState([]);
  const { lawerId, userId } = useSelector((data) => data.user);
  const currentLawyerId = teamMemberLawyerId || lawerId;

  // Memoize filtered sections to prevent unnecessary recalculations
  const filteredSections = useMemo(() => {
    return sections.filter((section) => {
      if (tabBtn === 1) {
        return section.id !== isDefaultSection;
      }
      return section.id === isDefaultSection;
    });
  }, [sections, tabBtn, isDefaultSection]);

  // Memoize the default section to avoid repeated lookups
  const defaultSection = useMemo(() => {
    return sections.find((section) => section.id === isDefaultSection);
  }, [sections, isDefaultSection]);

  // Update used fields types when default section changes
  useEffect(() => {
    if (defaultSection) {
      const defaultSectionFieldLabels = defaultSection.fields.map(
        (field) => field.label || field.name
      );

      setUsedFieldsTypes((prev) => {
        const newFields = prev.map((field) => ({
          ...field,
          disabled: defaultSectionFieldLabels.includes(
            field.label || field.name
          ),
        }));

        // Only update if fields actually changed
        return JSON.stringify(newFields) === JSON.stringify(prev)
          ? prev
          : newFields;
      });
    }
  }, [sections, isDefaultSection, setUsedFieldsTypes, defaultSection]);

  // Initialize quick create section when switching to tab 2
  useEffect(() => {
    if (tabBtn === 2 && !initialized.current) {
      initialized.current = true;

      // Get only REQUIRED fields from create new matter form
      const requiredFields = sections.reduce((acc, section) => {
        if (section.id !== isDefaultSection) {
          return [...acc, ...section.fields.filter((field) => field.required)];
        }
        return acc;
      }, []);

      setSections((prev) => {
        const existingDefault = prev.find((s) => s.id === isDefaultSection);
        const existingFieldIds = new Set(
          existingDefault?.fields.map((f) => f.id) || []
        );

        // Filter out already existing fields
        const newFields = requiredFields.filter(
          (field) => !existingFieldIds.has(field.id)
        );

        if (!existingDefault) {
          return [
            ...prev,
            {
              id: isDefaultSection,
              name: "Default Section",
              columns: 3,
              alignment: "top_to_bottom",
              fields: newFields,
            },
          ];
        }

        return prev.map((section) =>
          section.id === isDefaultSection
            ? { ...section, fields: [...section.fields, ...newFields] }
            : section
        );
      });

      setQuickCreateFields(requiredFields);
    }
  }, [tabBtn, sections, isDefaultSection, setSections]);

  /*
   * Quick Create functionality is currently disabled. The code below handles
   * dropping fields into the quick create form. It has been preserved for
   * future use but is no longer executed.
   */
  // const handleQuickCreateFieldDrop = useCallback(
  //   (e, targetSectionId) => {
  //     console.log("handleQuickCreateFieldDrop");
  //     e.preventDefault();
  //     const draggedItem = draggedItemRef.current;
  //     if (!draggedItem) return;
  //     const targetSection = sections.find((s) => s.id === targetSectionId);
  //     if (!targetSection) return;
  //     if (targetSectionId === isDefaultSection) {
  //       const isDuplicate = targetSection.fields.some(
  //         (field) =>
  //           field.id === draggedItem.id || field.label === draggedItem.label
  //       );
  //       if (isDuplicate) {
  //         toast.error("Field already exists in the section");
  //         return;
  //       }
  //       const newField = {
  //         ...draggedItem,
  //         required: draggedItem.required || false,
  //         isNew: true,
  //         sourceSectionId: null,
  //         sourceIndex: null,
  //       };
  //       setSections((prevSections) =>
  //         prevSections.map((section) => {
  //           if (section.id === targetSectionId) {
  //             return {
  //               ...section,
  //               fields: [...section.fields, newField],
  //             };
  //           }
  //           return section;
  //         })
  //       );
  //       setUsedFieldsTypes((prev) => {
  //         const existingFieldIndex = prev.findIndex(
  //           (field) => field.value === draggedItem.value
  //         );
  //         if (existingFieldIndex !== -1) {
  //           const updatedFields = [...prev];
  //           updatedFields[existingFieldIndex] = {
  //             ...updatedFields[existingFieldIndex],
  //             disabled: true,
  //             isUsed: true,
  //             isRemoved: false,
  //           };
  //           return updatedFields;
  //         }
  //         return prev;
  //       });
  //       toast.success("Field added to Quick Create form");
  //     } else {
  //       handleFieldDrop(e, targetSectionId);
  //     }
  //   },
  //   [
  //     sections,
  //     isDefaultSection,
  //     handleFieldDrop,
  //     draggedItemRef,
  //     setSections,
  //     setUsedFieldsTypes,
  //   ]
  // );

  const handleSave = useCallback(async () => {
    const formData = getFormData();
    if (
      !formData?.createNewMatter?.sections ||
      formData.createNewMatter.sections.length === 0
    ) {
      toast.error(
        "Please configure sections in Create New Matter template first"
      );
      return;
    }
    /*
     * Quick Create validation disabled
    if (
      !formData?.quickCreateNewMatter?.sections ||
      formData.quickCreateNewMatter.sections.length === 0
    ) {
      toast.error(
        "Please configure sections in Quick Create New Matter template first"
      );
      setSaveLoad(false);

      closeAllDialogs();
      return;
    }
    */

    try {
      const customFieldApiData = {
        created_by: userId,
        under_lawer: currentLawyerId,
        field_name: formData?.module,
        field_for: "case",
        field_data: {
          createNewMatter: formData?.createNewMatter,
          quickCreateNewMatter: formData?.quickCreateNewMatter,
          unUsedFieldsTypes: formData?.unUsedFieldsTypes,
          usedFieldsTypes: formData?.usedFieldsTypes,
        },
      };

      let customFieldId = null;
      if (isEditMode && editData?.custom_fields?.[0]?.id) {
        customFieldId = editData.custom_fields[0]?.id;
        await axios.patch(
          appendTeamMemberLawyerId(
            `${backend_api}custom-field/${customFieldId}/`,
            teamMemberLawyerId,
          ),
          customFieldApiData,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Token ${token}`,
            },
          }
        );
      } else {
        const customFieldApires = await axios.post(
          appendTeamMemberLawyerId(
            `${backend_api}custom-field/`,
            teamMemberLawyerId,
          ),
          customFieldApiData,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Token ${token}`,
            },
          }
        );
        customFieldId = customFieldApires?.data?.id;
      }

      if (customFieldId) {
        let linkedTemplateId = formData?.linked_templated;
        if (isEditMode && editData) {
          if (linkedTemplateId === editData.id || !editData.is_standard) {
            linkedTemplateId = editData.linked_templated ?? linkedTemplateId;
          } else if (editData.is_standard) {
            linkedTemplateId = editData.id;
          }
        }
        const templateOrgData = {
          created_by: userId,
          under_lawer: currentLawyerId,
          template_name: formData?.template_name,
          practice_area: formData?.practice_area,
          template_description: formData?.template_description,
          custom_fields: [customFieldId],
          linked_templated: linkedTemplateId,
        };

        if (isEditMode && editData?.id) {
          await axios.patch(
            appendTeamMemberLawyerId(
              `${backend_api}case/template-register/${editData.id}/`,
              teamMemberLawyerId,
            ),
            templateOrgData,
            {
              headers: {
                "Content-Type": "application/json",
                Authorization: `Token ${token}`,
              },
            }
          );
          toast.success("Template updated successfully");
        } else {
          await axios.post(
            appendTeamMemberLawyerId(
              `${backend_api}case/template-register/`,
              teamMemberLawyerId,
            ),
            templateOrgData,
            {
              headers: {
                "Content-Type": "application/json",
                Authorization: `Token ${token}`,
              },
            }
          );
          toast.success(
            <div>
              Matter template created successfully. Check your custom field limit.
              <Link
                to="/dashboard/settings/subscription-billing"
                style={{ color: "#fff", textDecoration: "underline" }}
              >
                click here.
              </Link>
            </div>
          );
        }

        dispatch(
          getCaseTemplate({
            url: `${backend_api}case/template-register/`,
            token: token,
            teamMemberLawyerId,
          })
        );
      }
    } catch (error) {
      toast.error(
        <div>
          {error?.response?.data?.detail || "Failed to create matter template"}{" "}
          <Link
            to="/dashboard/settings/subscription-billing"
            style={{ fontSize: "0.8rem", textDecoration: "underline" }}
          >
            Click Here
          </Link>
        </div>
      );
    } finally {
      closeAllDialogs();
    }
  }, [
    closeAllDialogs,
    dispatch,
    editData,
    getFormData,
    isEditMode,
    teamMemberLawyerId,
    token,
    userId,
    currentLawyerId,
  ]);

  // Memoize the preview handler
  const handlePreview = useCallback(() => {
    const formData = getFormData();

    showDialog(
      <Box>
        <PreviewForm
          tabBtn={tabBtn}
          formData={formData}
          handleSave={handleSave}
        />
      </Box>,
      {
        dialogTitle: "Preview Form",
        fullScreen: tabBtn === 1,
        width: tabBtn === 1 ? "100%" : "45%",
        formType: "matter",
      }
    );
  }, [tabBtn, showDialog, getFormData, handleSave]);

  return (
    <Box
      sx={{
        width: "75%",
        bgcolor: "#f7f9fc",
        overflowY: "auto",
        p: "0.5rem",
        display: "flex",
        flexDirection: "column",
        gap: "0.5rem",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            display: "flex",
            gap: "0.5rem",
            border: "1px solid #e5e5e5",
            p: "0.2rem",
            borderRadius: "0.5rem",
          }}
        >
          <Button
            variant={tabBtn === 1 ? "contained" : "text"}
            size="small"
            sx={{
              fontsize: "0.8rem",
              textTransform: "capitalize",
              transition: "all 0.2s ease",
            }}
            onClick={() => handleTabChange(1)}
          >
            Create New Matter
          </Button>
          {/**
           * Quick Create New Matter is currently not required.
           * Button is kept here for future reference.
           */}
          {/*
          <Button
            variant={tabBtn === 2 ? "contained" : "text"}
            size="small"
            sx={{
              textTransform: "capitalize",
              fontSize: "0.8rem",
              transition: "all 0.2s ease",
            }}
            onClick={() => handleTabChange(2)}
          >
            Quick Create New Matter
          </Button>
          */}
        </Box>

        <Box sx={{ display: "flex", gap: "1rem" }}>
          <Button
            id="preview_save_btn"
            variant="contained"
            size="small"
            onClick={handlePreview}
            sx={{ fontSize: "0.8rem" }}
          >
            Preview & Save
          </Button>
        </Box>
      </Box>

      {/* Drop area for sections */}
      <Box
        // id="drop_section_area"
        ref={scrollContainerRef}
        onDragOver={handleDragOver}
        onDragEnter={(e) => handleDragEnter(e, "section")}
        onDragLeave={handleDragLeave}
        onDrop={handleSectionDrop}
        sx={{
          borderRadius: "0.5rem",
          overflowY: "auto",
          height: "100%",
          p: "0.5rem",
          transition: "all 0.3s ease",
          display: "flex",
          flexDirection: "column",
          justifyContent: tabBtn === 2 ? "center" : "none",
          alignItems: tabBtn === 2 ? "center" : "none",
          gap: "1rem",
          scrollBehavior: "smooth",
          ...pageScrollStyle,
        }}
      >
        {/* Render filtered sections */}
        {filteredSections.map((section, index) => (
          <Section
            key={section.id}
            section={section}
            index={index}
            isDragging={isDragging}
            draggedItemRef={draggedItemRef}
            activeSectionDropIndex={activeSectionDropIndex}
            setActiveSectionDropIndex={setActiveSectionDropIndex}
            handleSectionDragStart={handleSectionDragStart}
            handleFieldDragStart={handleFieldDragStart}
            handleDragEnd={handleDragEnd}
            handleDragOver={handleDragOver}
            handleDragEnter={handleDragEnter}
            handleDragLeave={handleDragLeave}
            handleSectionDrop={handleSectionDrop}
            handleSectionReorderDrop={handleSectionReorderDrop}
            handleUpdateSectionName={handleUpdateSectionName}
            handleRemoveSection={handleRemoveSection}
            handleSectionMenuOpen={handleSectionMenuOpen}
            // Quick create drop handler disabled
            handleFieldDrop={handleFieldDrop}
            handleFieldReorderDrop={handleFieldReorderDrop}
            handleUpdateFieldLabel={handleUpdateFieldLabel}
            handleFieldMenuOpen={handleFieldMenuOpen}
            activeFieldDropData={activeFieldDropData}
            setActiveFieldDropData={setActiveFieldDropData}
            getFieldWidth={getFieldWidth}
            dragTarget={dragTarget}
            handleUpdateFieldProperty={handleUpdateFieldProperty}
            isDefaultSection={section.id === isDefaultSection}
            isFieldAvailableForDefaultSection={
              isFieldAvailableForDefaultSection
            }
          />
        ))}

        {/* Section drop area after existing sections - only for create new matter */}
        {tabBtn === 1 && (
          <Box
            id="drop_section_area"
            onDragOver={handleDragOver}
            onDragEnter={(e) => handleDragEnter(e, "section")}
            onDragLeave={handleDragLeave}
            onDrop={handleSectionDrop}
            sx={{
              minHeight: "4rem",
              borderRadius: "0.5rem",
              border: "1px dashed #e0e0e0",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor:
                dragTarget.type === "section"
                  ? "rgba(76, 175, 80, 0.1)"
                  : "#ffffff",
              color: "#757575",
              fontSize: "0.8rem",
              fontStyle: "italic",
            }}
          >
            Drag and drop sections here
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default React.memo(FormBuilder);
